/** Who holds a joining product's IDs, and what ops answer about it (plan 2026-10-11 product joins
 *  D4, D5): the pieces the product-sync "Combine into one tour" dialog and the catalogue settings
 *  drawer's `tour.join` share, both fed by the same member shape (`ConsoleProductJoinMember`).
 *
 *  - {@link holdersOfMember}: the tours other than the joined one that hold the member's IDs, from
 *    the server's preview (parked names, take-overs and deleted holders included);
 *  - {@link OwnershipChoice} / {@link answered}: the required choice with no default, move or not,
 *    which only answers while it covers every tour holding the IDs now;
 *  - {@link OwnershipBlock}: the warning with that choice, the consequences of a move worded from
 *    the preview's `parked_as` and `impact`, and the booking-move checkbox (off by default, hidden
 *    unless the user may move bookings);
 *  - {@link BookingMoveAnswers}: the Edit-booking task's two questions, no defaults. */
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Fragment, type ReactNode } from 'react';

import type { BookingMoveChoice, ProductJoinImpact, ProductJoinsPreviewMember } from '@/api/sync';

export interface HeldValue {
  value: string;
  kind: 'primary' | 'alias' | null;
  /** A tour's own ID: what a move renames it to (`OFF/<id>`); server only. */
  parkedAs: string | null;
  /** An import-owned additional ID: the import stops maintaining it. */
  managed: boolean;
  compoundSiblings: string[];
}

export interface HoldingTour {
  /** Null when only soft-deleted tours hold the values. */
  id: number | null;
  name: string | null;
  values: HeldValue[];
}

/** Group held values by the tour holding them, in order. */
export function groupHolders(entries: Array<{ id: number | null; name: string | null; value: HeldValue }>): HoldingTour[] {
  const byTour = new Map<string, HoldingTour>();
  for (const { id, name, value } of entries) {
    const key = String(id);
    const tour = byTour.get(key) ?? { id, name, values: [] };
    tour.values.push(value);
    byTour.set(key, tour);
  }
  return [...byTour.values()];
}

/** The tours other than the joined one that hold a member's IDs, as the server's preview sees them
 *  (an ID already an additional ID of the joined tour is not held elsewhere). */
export function holdersOfMember(member: ProductJoinsPreviewMember): HoldingTour[] {
  return groupHolders(
    member.ext_ids.flatMap((e) =>
      !e.owner || e.action === 'already'
        ? []
        : [
            {
              id: e.owner.tour_id,
              name: e.owner.tour_name,
              value: {
                value: e.custom_id,
                kind: e.owner.kind,
                parkedAs: e.owner.parked_as,
                managed: e.owner.managed,
                compoundSiblings: e.owner.compound_siblings,
              },
            },
          ],
    ),
  );
}

/** The live tours a move would take the IDs from: the consent. */
export function consentTours(tours: HoldingTour[]): number[] {
  return [...new Set(tours.flatMap((t) => (t.id === null ? [] : [t.id])))].sort((a, b) => a - b);
}

export type OwnershipChoice = { kind: 'move'; tours: number[] } | { kind: 'leave' };

/** A choice still answers the question only while it covers every tour that
 *  holds the IDs now: a move consented for tour X says nothing about tour Y. */
export function answered(choice: OwnershipChoice | undefined, tours: HoldingTour[]): boolean {
  if (!choice) return false;
  if (choice.kind === 'leave') return true;
  return consentTours(tours).every((id) => choice.tours.includes(id));
}

export function sumImpact(impact: ProductJoinImpact[]) {
  const total = { future: 0, checkedIn: 0, unknown: 0, past: 0, subs: 0 };
  for (const i of impact) {
    total.future += i.future_bookings;
    total.checkedIn += i.checked_in_future;
    total.unknown += i.unknown_product_future;
    total.past += i.past_bookings;
    total.subs += i.sub_bookings;
  }
  return total;
}

export const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
export const tourLabel = (t: { id: number | null; name: string | null }) =>
  t.name || (t.id === null ? 'a deleted tour' : `tour #${t.id}`);
export const listLabels = (labels: string[]) =>
  labels.length <= 1
    ? (labels[0] ?? '')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

// ---------------------------------------------------------------------------
// The ownership warning (D4) and the booking move (D5)
// ---------------------------------------------------------------------------

export interface OwnershipBlockProps {
  /** The joining product. */
  name: string;
  integration: string;
  holders: HoldingTour[];
  choice: OwnershipChoice | undefined;
  impact: ProductJoinImpact[];
  canMoveBookings: boolean;
  moveBookings: boolean;
  onChoose: (kind: OwnershipChoice['kind']) => void;
  onMoveBookings: (on: boolean) => void;
  /** The tour the IDs join, in words (default "this tour"). */
  destination?: string;
  /** The "not moving" answer (default: "Leave ‹name› out", the product-sync wording). */
  leaveLabel?: ReactNode;
}

export function OwnershipBlock({
  name,
  integration,
  holders,
  choice,
  impact,
  canMoveBookings,
  moveBookings,
  onChoose,
  onMoveBookings,
  destination = 'this tour',
  leaveLabel,
}: OwnershipBlockProps) {
  const tours = listLabels(holders.map(tourLabel));
  const allOwn = holders.every((t) => t.values.every((v) => v.kind === 'primary'));
  const allAlias = holders.every((t) => t.values.every((v) => v.kind === 'alias'));
  const total = sumImpact(impact);
  const moving = choice?.kind === 'move';
  const impactTours = listLabels(
    impact.filter((i) => i.future_bookings > 0).map((i) => tourLabel({ id: i.tour_id, name: i.tour_name })),
  );

  const lines: ReactNode[] = [];
  if (!moving && (total.future > 0 || total.past > 0)) {
    lines.push(
      `${[
        total.future > 0 && plural(total.future, 'future booking', 'future bookings'),
        total.past > 0 && plural(total.past, 'past booking', 'past bookings'),
      ]
        .filter(Boolean)
        .join(' and ')} of ${name} ${total.future + total.past === 1 ? 'is' : 'are'} on ${listLabels(
        impact.map((i) => tourLabel({ id: i.tour_id, name: i.tour_name })),
      )}.`,
    );
  }
  if (moving) {
    for (const t of holders) {
      for (const v of t.values) {
        if (v.kind === 'primary') {
          lines.push(
            <>
              Tour {tourLabel(t)} stops using <code>{v.value}</code>; its ID becomes{' '}
              {/* The parked name is the server's: `OFF/<id>`, or `-2`, `-3`… past a clash. */}
              {v.parkedAs ? <code>{v.parkedAs}</code> : 'an OFF/ name'}.
            </>,
          );
        } else if (v.kind === 'alias') {
          lines.push(
            <>
              Tour {tourLabel(t)} stops using <code>{v.value}</code>: the additional ID moves to{' '}
              {destination}, with its tags.
              {v.managed && ` The ${integration} import will stop maintaining it.`}
            </>,
          );
        }
      }
      const siblings = t.values.flatMap((v) => v.compoundSiblings);
      if (siblings.length) {
        lines.push(
          <>
            {siblings.map((s, i) => (
              <Fragment key={s}>
                {i > 0 && ', '}
                <code>{s}</code>
              </Fragment>
            ))}{' '}
            {siblings.length === 1 ? 'stays' : 'stay'} on {tourLabel(t)} and keep
            {siblings.length === 1 ? 's' : ''} matching it.
          </>,
        );
      }
    }
    for (const i of impact) {
      const where = tourLabel({ id: i.tour_id, name: i.tour_name });
      if (i.future_bookings > 0) {
        lines.push(
          <>
            {plural(i.future_bookings, 'future booking', 'future bookings')} of {name}{' '}
            {i.future_bookings === 1 ? 'is' : 'are'} on {where}:{' '}
            {moveBookings
              ? `${i.future_bookings === 1 ? 'it moves' : 'they move'} now.`
              : `${i.future_bookings === 1 ? 'it moves' : 'they move one by one'} when the vendor next sends ${i.future_bookings === 1 ? 'it; it loses its' : 'them; each loses its'} tags, check-in marks included.`}
          </>,
        );
      }
      if (i.past_bookings > 0) {
        lines.push(`${plural(i.past_bookings, 'past booking stays', 'past bookings stay')} on ${where}.`);
      }
      if (i.checked_in_future > 0) {
        lines.push(
          `${plural(i.checked_in_future, 'checked-in booking stays', 'checked-in bookings stay')} on ${where}.`,
        );
      }
    }
  }

  const stays = [
    total.unknown > 0 && `${total.unknown} with an unknown product`,
    total.checkedIn > 0 && `${total.checkedIn} checked in`,
    total.past > 0 && `${total.past} past`,
  ].filter(Boolean);

  return (
    <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <AlertTitle>
        {allOwn
          ? `${name}'s ID is already the own ID of ${tours}`
          : allAlias
            ? `${name}'s ID is already an additional ID of ${tours}`
            : `${name}'s IDs are already used by ${tours}`}
      </AlertTitle>
      <RadioGroup
        aria-label={`What to do with ${name}`}
        value={choice?.kind ?? ''}
        onChange={(e) => onChoose(e.target.value as OwnershipChoice['kind'])}
      >
        <FormControlLabel
          value="leave"
          control={<Radio size="small" />}
          label={
            leaveLabel ?? (
              <>
                Leave {name} out
                <Typography variant="caption" color="text.secondary" component="span">
                  {' '}
                  ({allOwn
                    ? `it stays on ${tours} and syncs as itself`
                    : allAlias
                      ? `it stays an additional ID of ${tours} and is not built`
                      : 'it syncs as itself; a product whose ID is another tour’s additional ID is not built'}
                  )
                </Typography>
              </>
            )
          }
        />
        <FormControlLabel
          value="move"
          control={<Radio size="small" />}
          label={`Move ${name} to ${destination}`}
        />
      </RadioGroup>
      {lines.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {lines.map((l, i) => (
            <Typography key={i} component="li" variant="body2">
              {l}
            </Typography>
          ))}
        </Box>
      )}
      {moving && canMoveBookings && total.future > 0 && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={moveBookings}
                onChange={(e) => onMoveBookings(e.target.checked)}
              />
            }
            label={`Also move ${name}'s ${plural(total.future, 'future booking', 'future bookings')} from ${impactTours} to ${destination}`}
          />
          {(stays.length > 0 || total.subs > 0) && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4 }}>
              {stays.length > 0 && `Stays on ${impactTours}: ${stays.join(', ')}.`}
              {total.subs > 0 &&
                ` ${plural(total.subs, 'sub-booking moves', 'sub-bookings move')} with ${total.future === 1 ? 'it' : 'them'}.`}
            </Typography>
          )}
        </Box>
      )}
    </Alert>
  );
}

/** The Edit-booking task's two questions for a booking move, required, with no default. */
export function BookingMoveAnswers({
  answers,
  onChange,
  oldTours,
  title,
}: {
  answers: Partial<BookingMoveChoice>;
  onChange: (next: Partial<BookingMoveChoice>) => void;
  /** The tours the bookings leave (the tags question names a single one). */
  oldTours: Array<{ id: number | null; name: string | null }>;
  title: string;
}) {
  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
        <FormControl>
          <FormLabel id="booking-move-events">Notify guests of the change</FormLabel>
          <RadioGroup
            row
            aria-labelledby="booking-move-events"
            value={answers.update_events ?? ''}
            onChange={(e) =>
              onChange({ ...answers, update_events: e.target.value as BookingMoveChoice['update_events'] })
            }
          >
            <FormControlLabel value="emit" control={<Radio size="small" />} label="Send update events" />
            <FormControlLabel value="suppress" control={<Radio size="small" />} label="Don’t" />
          </RadioGroup>
        </FormControl>
        <FormControl>
          <FormLabel id="booking-move-tags">
            Tags that only {oldTours.length === 1 ? tourLabel(oldTours[0]!) : 'the old tour'} uses
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="booking-move-tags"
            value={answers.old_tour_tags ?? ''}
            onChange={(e) =>
              onChange({ ...answers, old_tour_tags: e.target.value as BookingMoveChoice['old_tour_tags'] })
            }
          >
            <FormControlLabel value="drop" control={<Radio size="small" />} label="Drop" />
            <FormControlLabel value="keep" control={<Radio size="small" />} label="Keep" />
          </RadioGroup>
        </FormControl>
      </Stack>
    </Box>
  );
}
